import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthFeil, type AdminAuth, type Faktorer, type Sesjonskrav, type Sesjonsspoerring, type TotpOppsett } from './typer.ts';

/**
 * Tilstandsmaskinen for innloggingen. Fem synlige tilstander, hver med sitt
 * skjermbilde; overgangene styres av det leverandøren OG leietakerens innstilling
 * sier om sesjonen, ikke av hva panelet husker:
 *
 *   laster → utlogget → kode-sendt → (sesjon)
 *   sesjon aal2                          → innlogget
 *   sesjon aal1, luken sier utløpt        → utlogget, med melding
 *   sesjon aal1, luken svarer 401         → utlogget, med melding om tilgang
 *   sesjon aal1, leietakeren krever ikke → innlogget            (én faktor er nok)
 *   sesjon aal1, kan bli aal2            → tofaktor-utfordring  (har autentiseringsapp)
 *   sesjon aal1, kan ikke                → tofaktor-oppsett     (må sette opp — forklart lås, K8)
 *
 * Steget som kom til 2026-09-21: ved `aal1` kan provideren ikke lenger avgjøre alt fra
 * leverandørens sesjon. Tofaktor er leietakerens valg, og svaret finnes bare hos API-et —
 * i luken `GET /v1/admin/session`, som er det ENESTE kallet som er lov før den har svart.
 * Luken bærer to felter, og begge brukes her: kravet velger skjerm, og `sessionExpiresAt`
 * avgjør om det er en skjerm å velge i det hele tatt.
 *
 * API-et svarer uavhengig: 401 logger ut med melding, 403 mfa_required sender hit
 * for ny vurdering (src/api/Sesjonsvakt.tsx). Kommer den koden til en sesjon som alt
 * slapp inn, er kravet nettopp slått PÅ av leietakerens eier — provideren spør luken på
 * nytt og sender brukeren til utfordringen.
 */
export type AuthTilstand =
  | { navn: 'laster' }
  | { navn: 'utlogget'; melding?: string }
  | { navn: 'kode-sendt'; epost: string }
  /**
   * `ukjentKrav` skiller «luken svarte nei» fra «luken svarte ikke». Begge ender på denne
   * skjermen — kravet gjelder når svaret uteblir — men de er ikke det samme, og brukeren
   * skal kunne se forskjellen: uten flagget ser en server som er nede ut som et krav
   * leietakeren har satt. Skjermen bærer da én setning om at vi ikke fikk kontakt.
   */
  | { navn: 'tofaktor-oppsett'; ukjentKrav?: true }
  | { navn: 'tofaktor-utfordring'; ukjentKrav?: true }
  /** `epost` er kontoens adresse fra leverandøren — vist til brukeren selv i skallet (DD-48). */
  | { navn: 'innlogget'; epost?: string };

export interface AuthKontekst {
  tilstand: AuthTilstand;
  sendKode(epost: string): Promise<void>;
  bekreftKode(kode: string): Promise<void>;
  tilbakeTilEpost(): void;
  loggInnMedGoogle(): Promise<void>;
  startOppsett(): Promise<TotpOppsett>;
  bekreftOppsett(faktorId: string, kode: string): Promise<void>;
  bekreftUtfordring(kode: string): Promise<void>;
  /** Kan nettleseren bekrefte med enheten? Synkron — skjermen trenger svaret før første tegning. */
  stoetterEnhet(): boolean;
  hentFaktorer(): Promise<Faktorer>;
  settOppEnhet(): Promise<void>;
  bekreftMedEnhet(): Promise<void>;
  /** Avregistrerer én metode. «Min innlogging» eier regelen om at den siste aldri fjernes. */
  fjernFaktor(id: string): Promise<void>;
  /** Leses friskt per API-kall. Null når ingen sesjon finnes. */
  hentToken(): Promise<string | null>;
  /** Vurder sesjonen på nytt — etter 403 mfa_required, eller når fanen får fokus. */
  oppdater(): Promise<void>;
  loggUt(melding?: string): Promise<void>;
}

const Kontekst = createContext<AuthKontekst | null>(null);

/**
 * Når luken svarer 401. Koden sier ikke hvilken av de tre grunnene det er (konto uten sete,
 * deaktivert, økt over åtte timer), så setningen dekker dem i den rekkefølgen de er vanligst,
 * og sier hvem som kan hjelpe. Aldri HVOR adressen eventuelt har tilgang (K2).
 */
export const INGEN_TILGANG = 'Kontoen din har ikke tilgang til panelet nå. Logg inn på nytt — og er du ny, ber du eieren hos dere om tilgang.';

export function AuthProvider({ auth, sesjon: spoerSesjon, children }: { auth: AdminAuth; sesjon: Sesjonsspoerring; children: ReactNode }) {
  const [tilstand, setTilstand] = useState<AuthTilstand>({ navn: 'laster' });
  const epostRef = useRef<string | null>(null);
  /**
   * Hver vurdering får et nummer. Vurderingen er ikke lenger synkron — den venter på
   * luken — og `paaEndring` kan fyre igjen mens den venter (fornyet token, annen fane).
   * Bare den siste får sette tilstanden; et gammelt svar skal aldri kunne skrive over et
   * nyere. Uten dette kunne en utlogging bli overskrevet av «innlogget» fra kallet før.
   */
  const rundeRef = useRef(0);

  /**
   * Spør luken. `null` betyr at den ikke svarte — nettverksfeil, 500, eller et svar uten det
   * boolske feltet. `'ingen-tilgang'` er 401: luken SVARTE, og svaret er at tokenet ikke gir
   * tilgang (konto uten sete, deaktivert, eller en økt over åtte timer). Det er ikke det samme
   * som taushet — da er det ingen tofaktor å be om (observert 2026-10-29). Feilen svelges
   * ellers med vilje og uten logg: handlingen står i `vurder()`, og skjermen forklarer seg selv.
   */
  const spoer = useCallback(
    async (token: string): Promise<Sesjonskrav | 'ingen-tilgang' | null> => {
      try {
        return await spoerSesjon(token);
      } catch (e) {
        return (e as { status?: unknown } | null)?.status === 401 ? 'ingen-tilgang' : null;
      }
    },
    [spoerSesjon],
  );

  const vurder = useCallback(async () => {
    const runde = (rundeRef.current += 1);
    const gjelder = () => rundeRef.current === runde;
    const sesjon = await auth.hentSesjon();
    if (!gjelder()) return;
    if (!sesjon) {
      setTilstand((t) => (t.navn === 'kode-sendt' || t.navn === 'utlogget' ? t : { navn: 'utlogget' }));
      return;
    }
    const innlogget = (): AuthTilstand => (sesjon.epost ? { navn: 'innlogget', epost: sesjon.epost } : { navn: 'innlogget' });
    if (sesjon.aal === 'aal2') {
      // Kravet er oppfylt uansett hva leietakeren har valgt: luken spørres ikke.
      setTilstand(innlogget());
      return;
    }

    const svar = await spoer(sesjon.accessToken);
    if (!gjelder()) return;

    /**
     * 401 fra luken: tokenet er gyldig hos leverandøren, men gir ingen tilgang i Innspel. Ut,
     * med en setning som sier hva det kan bety og hvem som kan gjøre noe med det — samme
     * rekkefølge som utløpt økt under (tilstand før utlogging). Strengere enn tofaktor, så
     * regelen «lukket luke» brytes ikke.
     */
    if (svar === 'ingen-tilgang') {
      epostRef.current = null;
      setTilstand({ navn: 'utlogget', melding: INGEN_TILGANG });
      await auth.loggUt();
      return;
    }
    const krav = svar;

    /**
     * Sesjonen kan ha dødd mens tofaktorskjermen sto åpen, eller før den rakk å vises.
     * Da skal hun til innloggingen og ikke til en utfordring hun aldri kan fullføre — det
     * er hele grunnen til at `sessionExpiresAt` er med i luken. Tilstanden settes FØR
     * utloggingen: porten varsler, `vurder()` kjører igjen med en ny runde, og ser da
     * ingen sesjon — den grenen lar en «utlogget» med melding stå.
     *
     * En dato som ikke lar seg lese gir `NaN`, og `NaN <= nå` er usant: da skjer
     * ingenting her. Åtte-timersgrensen håndheves uansett server-side ved neste kall, og
     * å logge noen ut på et tall vi ikke forsto ville vært å gjette.
     */
    if (krav && Date.parse(krav.sessionExpiresAt) <= Date.now()) {
      epostRef.current = null;
      setTilstand({ navn: 'utlogget', melding: 'Økten er over. Logg inn på nytt.' });
      await auth.loggUt();
      return;
    }

    /**
     * **Regelen «lukket luke».** Alt som ikke er et tydelig «nei» leses som et ja.
     * Serveren feiler lukket (`ErrorCode.MfaRequired`), og klienten gjør nøyaktig det
     * samme — et krav som forsvinner fordi et kall gikk galt, er ikke et krav.
     */
    const kreves = krav?.adminMfaRequired ?? true;
    if (!kreves) {
      setTilstand(innlogget());
      return;
    }
    // Skjermen skal si hvorfor den er der, og «vi vet ikke» er et annet hvorfor enn «de krever det».
    const ukjent = krav === null ? ({ ukjentKrav: true } as const) : {};
    if (sesjon.nesteAal === 'aal2') setTilstand({ navn: 'tofaktor-utfordring', ...ukjent });
    else setTilstand({ navn: 'tofaktor-oppsett', ...ukjent });
  }, [auth, spoer]);

  useEffect(() => {
    void vurder();
    return auth.paaEndring(() => void vurder());
  }, [auth, vurder]);

  const verdi = useMemo<AuthKontekst>(
    () => ({
      tilstand,
      async sendKode(epost) {
        await auth.sendKode(epost);
        epostRef.current = epost;
        setTilstand({ navn: 'kode-sendt', epost });
      },
      async bekreftKode(kode) {
        const epost = epostRef.current;
        if (!epost) throw new AuthFeil('Skriv inn e-postadressen først.');
        await auth.bekreftKode(epost, kode);
        await vurder();
      },
      tilbakeTilEpost() {
        setTilstand({ navn: 'utlogget' });
      },
      loggInnMedGoogle: () => auth.loggInnMedGoogle(),
      startOppsett: () => auth.startOppsett(),
      async bekreftOppsett(faktorId, kode) {
        await auth.bekreftOppsett(faktorId, kode);
        await vurder();
      },
      async bekreftUtfordring(kode) {
        await auth.bekreftUtfordring(kode);
        await vurder();
      },
      // Enhetssporet speiler appens: porten gjør seremonien, og tilstanden hentes
      // ETTERPÅ fra leverandøren. Skjermen setter aldri aal2 selv — den spør (K8).
      stoetterEnhet: () => auth.stoetterEnhet(),
      hentFaktorer: () => auth.hentFaktorer(),
      async settOppEnhet() {
        await auth.settOppEnhet();
        await vurder();
      },
      async bekreftMedEnhet() {
        await auth.bekreftMedEnhet();
        await vurder();
      },
      // Sesjonen vurderes på nytt etterpå. Å fjerne én av to metoder senker den ikke fra
      // aal2 — men å la panelet tro noe om nivået uten å spørre er nettopp det denne
      // provideren finnes for å unngå.
      async fjernFaktor(id) {
        await auth.fjernFaktor(id);
        await vurder();
      },
      async hentToken() {
        return (await auth.hentSesjon())?.accessToken ?? null;
      },
      oppdater: vurder,
      async loggUt(melding) {
        await auth.loggUt();
        epostRef.current = null;
        setTilstand(melding ? { navn: 'utlogget', melding } : { navn: 'utlogget' });
      },
    }),
    [auth, tilstand, vurder],
  );

  return <Kontekst.Provider value={verdi}>{children}</Kontekst.Provider>;
}

export function useAuth(): AuthKontekst {
  const k = useContext(Kontekst);
  if (!k) throw new Error('[innspel] useAuth utenfor AuthProvider.');
  return k;
}
